import { z } from 'zod';

export const SVC_STAGES = ['NEW', 'ASSESSING', 'IN_REPAIR', 'RESOLVED', 'CLOSED', 'REJECTED'] as const;
export type SvcStage = (typeof SVC_STAGES)[number];
export const SVC_STAGE_LABELS: Record<SvcStage, string> = { NEW: 'New', ASSESSING: 'Assessing', IN_REPAIR: 'In repair', RESOLVED: 'Resolved', CLOSED: 'Closed', REJECTED: 'Rejected' };
export const SVC_CHANNELS = { PHONE: 'Phone', WHATSAPP: 'WhatsApp', SHOWROOM: 'Showroom', EMAIL: 'Email', INSTALLER: 'Reported by installer' } as const;
export const SVC_CATEGORIES = { DEFECT: 'Defect / broken', DAMAGE: 'Damaged in transit', MISSING_PART: 'Missing part', COMFORT: 'Comfort (mattress)', INSTALLATION: 'Installation problem', OTHER: 'Other' } as const;
export const SVC_PRIORITIES = { LOW: 'Low', NORMAL: 'Normal', URGENT: 'Urgent' } as const;
export const SVC_WARRANTY = { UNKNOWN: 'Not checked', IN: 'In warranty', OUT: 'Out of warranty', GOODWILL: 'Goodwill (free)' } as const;
export const SVC_ROOT_CAUSES = { MATERIAL: 'Material / supplier', WORKMANSHIP: 'Workmanship', DESIGN: 'Design', TRANSIT: 'Transit / handling', INSTALLATION: 'Installation', MISUSE: 'Customer use', NO_FAULT: 'No fault found' } as const;
export const SVC_RESOLUTIONS = { REPAIR_SITE: 'Repair at the site', REPAIR_FACTORY: 'Repair in the factory', REPLACE: 'Replace', CREDIT: 'Credit / refund', NO_ACTION: 'No action needed' } as const;
export type SvcWarranty = keyof typeof SVC_WARRANTY;
export type SvcRootCause = keyof typeof SVC_ROOT_CAUSES;
export type SvcResolution = keyof typeof SVC_RESOLUTIONS;

const uuid = z.string().uuid();
const optUuid = uuid.nullable().default(null);
const keys = <T extends Record<string, string>>(o: T) => Object.keys(o) as [keyof T & string, ...(keyof T & string)[]];

export const svcTicketSchema = z
  .object({
    customerId: optUuid,
    contactName: z.string().trim().max(150).default(''),
    phone: z.string().trim().max(40).default(''),
    siteAddress: z.string().trim().max(500).default(''),
    channel: z.enum(keys(SVC_CHANNELS)).default('PHONE'),
    salesOrderId: optUuid,
    soLineId: optUuid,
    itemId: optUuid,
    serial: z.string().trim().max(60).nullable().default(null),
    qty: z.coerce.number().positive('Above 0').default(1),
    category: z.enum(keys(SVC_CATEGORIES)).default('DEFECT'),
    description: z.string().trim().min(5, 'Describe the problem').max(2000),
    priority: z.enum(keys(SVC_PRIORITIES)).default('NORMAL'),
  })
  .refine((v) => !!v.customerId || v.contactName.length >= 2, { path: ['contactName'], message: 'Pick the customer or type a name' });
export type SvcTicketInput = z.infer<typeof svcTicketSchema>;

export const svcAssessSchema = z
  .object({
    warranty: z.enum(['IN', 'OUT', 'GOODWILL']),
    warrantyNote: z.string().trim().max(500).default(''),
    rootCause: z.enum(keys(SVC_ROOT_CAUSES)),
    responsibleDeptId: optUuid,
    resolution: z.enum(keys(SVC_RESOLUTIONS)),
    assessment: z.string().trim().min(3, 'Write what was found').max(2000),
  });
export type SvcAssessInput = z.infer<typeof svcAssessSchema>;

export const svcNoteSchema = z.object({ text: z.string().trim().min(1, 'Write something').max(2000), kind: z.enum(['NOTE', 'CALL', 'CUSTOMER']).default('NOTE') });
export const svcResolveSchema = z.object({ note: z.string().trim().min(3, 'Say what was done').max(1000) });
export const svcCloseSchema = z.object({ rating: z.coerce.number().int().min(1).max(5).nullable().default(null), feedback: z.string().trim().max(1000).default('') });
export const svcRejectSchema = z.object({ reason: z.string().trim().min(3, 'Give the reason').max(1000) });
export const svcVisitSchema = z.object({ preferredDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/).nullable().default(null), notes: z.string().trim().max(500).default('') });
export const svcRepairJobSchema = z.object({ routeId: optUuid, notes: z.string().trim().max(500).default('') });
export const svcChargeSchema = z.object({ amount: z.coerce.number().positive('Above 0'), description: z.string().trim().min(3).max(300) });

/** One delivered thing a complaint can be about: found by serial, order number, customer or phone. */
export interface SvcLookupHit {
  customer: { id: string; name: string } | null;
  phone: string;
  siteAddress: string;
  salesOrder: { id: string; number: string | null };
  soLineId: string;
  item: { id: string; code: string; name: string } | null;
  description: string;
  qty: number;
  serial: string | null;
  delivery: { id: string; number: string | null; date: string } | null;
  installedOn: string | null;
  warrantyStart: string | null;
  warrantyEnd: string | null;
  warrantyMonths: number;
  inWarranty: boolean | null;
  openTickets: number;
}

export interface SvcTraceJob {
  id: string;
  number: string | null;
  repair: boolean;
  completedAt: string | null;
  operations: { name: string; people: string[]; completedAt: string | null; rework: boolean }[];
  qc: { round: number; result: string; notes: string; by: string; at: string }[];
  materials: { code: string; name: string; qty: number }[];
}
export interface SvcTrace {
  jobs: SvcTraceJob[];
  mattress: { serial: string; orderId: string; orderNumber: string | null; plannedDate: string; stages: { stage: string; date: string; people: string[]; good: number; rework: number; rejected: number }[] } | null;
  installation: { id: string; number: string | null; handedOverAt: string | null; crew: string[]; snags: number } | null;
  /** Other tickets on the same item / serial: a repeat failure. */
  related: { id: string; number: string | null; stage: SvcStage; opened: string; category: string }[];
}

export interface SvcTicketDetail {
  id: string;
  number: string | null;
  docDate: string;
  openedAt: string;
  stage: SvcStage;
  customer: { id: string; name: string } | null;
  contactName: string;
  phone: string;
  siteAddress: string;
  channel: keyof typeof SVC_CHANNELS;
  salesOrder: { id: string; number: string | null } | null;
  soLineId: string | null;
  delivery: { id: string; number: string | null; date: string } | null;
  item: { id: string; code: string; name: string } | null;
  serial: string | null;
  qty: number;
  category: keyof typeof SVC_CATEGORIES;
  description: string;
  priority: keyof typeof SVC_PRIORITIES;
  warranty: SvcWarranty;
  warrantyStart: string | null;
  warrantyEnd: string | null;
  /** What the dates say (before any decision). */
  warrantySuggested: 'IN' | 'OUT' | null;
  warrantyNote: string;
  rootCause: SvcRootCause | '';
  responsibleDept: { id: string; name: string } | null;
  resolution: SvcResolution | '';
  assessment: string;
  assignedTo: { id: string; name: string } | null;
  repairJob: { id: string; number: string | null; stage: string } | null;
  serviceVisit: { id: string; number: string | null; stage: string; nextVisit: string | null } | null;
  replacementOrder: { id: string; number: string | null; status: string } | null;
  chargeQuotation: { id: string; number: string | null; status: string; total: number } | null;
  cost: { repair: number; visits: number; replacement: number; total: number; frozen: boolean };
  responseDue: string;
  resolveDue: string;
  late: boolean;
  firstResponseAt: string | null;
  resolvedAt: string | null;
  closedAt: string | null;
  resolutionNote: string;
  rating: number | null;
  feedback: string;
  notes: { id: string; kind: string; text: string; by: string; at: string }[];
  trace: SvcTrace;
  photos: number;
}

export interface SvcTicketListItem {
  id: string;
  number: string | null;
  opened: string;
  stage: SvcStage;
  customer: string;
  item: string;
  serial: string | null;
  category: string;
  priority: string;
  warranty: SvcWarranty;
  resolution: string;
  assignedTo: string | null;
  resolveDue: string;
  late: boolean;
  ageDays: number;
}

export interface SvcBoardData {
  columns: { stage: SvcStage; label: string; cards: SvcTicketListItem[] }[];
  kpis: { open: number; late: number; inWarrantyPct: number | null; avgResolveDays: number | null; costThisMonth: number; rating: number | null };
}
